<template>
  <q-form class="row q-col-gutter-x-lg" @submit.prevent="onSubmit">
    <PInput
      v-model="formData.firstName"
      label="First Name"
      class="col-6"
      placeholder="Enter first name"
      required
      :error="firstNameError"
      maxlength="50"
      @update:model-value="onFirstNameInput"
    />
    <PInput
      v-model="formData.lastName"
      label="Last Name"
      class="col-6"
      placeholder="Enter last name"
      maxlength="50"
      @update:model-value="onLastNameInput"
    />
    <PInput
      v-model="formData.phone"
      :validations="{ required: true, type: 'phoneNumber' }"
      class="col-6 prepend-bg"
      input-class="ellipsis"
      label="Contact No."
      maxlength="10"
      placeholder="Enter mobile number"
      type="tel"
      @update:model-value="checkIfCoreContact"
    >
      <template #prepend>
        <span class="text-sm">{{ isdCode }}</span>
      </template>
    </PInput>

    <PSelectInput
      v-model="formData.role"
      label="Tag"
      class="col-6"
      placeholder="Select tag"
      clearable
      :disable="isRoleSelectorDisabled"
      :options="ROLE_OPTIONS"
    />

    <div class="col-12">
      <PBtn
        :fluid="false"
        :loading="isLoading"
        label="Add"
        class="flex q-ml-auto q-mt-sm"
        :disable="computedAddButtonDisabled"
        type="submit"
      />
    </div>
  </q-form>
</template>

<script setup>
import { index } from "src/api/user";
import { DEFAULT_ISD_CODE } from "../constants";
import { isValidPhoneNumber } from "src/assets/scripts/utilities/customValidators";

const emit = defineEmits(["contact-created"]);

const props = defineProps({
  contactNumber: {
    type: String,
    default: "",
  },
  firstName: {
    type: String,
    default: "",
  },
  lastName: {
    type: String,
    default: "",
  },
});

const { createConversationContact, createCustomContact } =
  useChatContactUtils();

const ROLE_OPTIONS = [
  { label: "Customer", value: "buyer" },
  { label: "Vendor", value: "seller" },
  { label: "Agent", value: "agent" },
];

const isLoading = ref(false);
const isdCode = ref(DEFAULT_ISD_CODE);

const formData = ref({
  firstName: props.firstName.trim(),
  lastName: props.lastName.trim(),
  phone: props.contactNumber || "",
  role: "",
});

const firstNameError = ref(false);

const isValidFirstName = (name) => !!name && /^[A-Za-z]+$/.test(name);

const onFirstNameInput = (val) => {
  formData.value.firstName = val.trim().replaceAll(/\s/g, "");

  firstNameError.value = !isValidFirstName(formData.value.firstName);
};

const onLastNameInput = (val) => {
  formData.value.lastName = val.trim().replaceAll(/\s/g, "");
};

const computedAddButtonDisabled = computed(
  () =>
    !isValidFirstName(formData.value.firstName) ||
    formData.value.phone?.length !== 10,
);

const coreContact = ref(null);
const checkingIfCoreContact = ref(false);

const isRoleSelectorDisabled = computed(
  () => unref(checkingIfCoreContact) || Boolean(unref(coreContact)?.role),
);

const checkIfCoreContact = async (phone) => {
  try {
    if (isValidPhoneNumber(phone)) {
      checkingIfCoreContact.value = true;

      const response = await index({
        params: {
          search: phone,
        },
      });

      coreContact.value = response.data.data.data?.[0] || null;

      if (
        ROLE_OPTIONS.some(
          (roleOption) => roleOption.value === unref(coreContact)?.role,
        )
      ) {
        formData.value.role = unref(coreContact).role;
      } else {
        coreContact.value = null;
      }
    } else {
      coreContact.value = null;

      formData.value.role = "";
    }
  } catch (error) {
    console.error("Error checking core contact:", error);

    coreContact.value = null;
  } finally {
    checkingIfCoreContact.value = false;
  }
};

const { purchaseOrder } = storeToRefs(usePurchaseOrderStore());

const linkedTo = computed(() => {
  const { _id, shop_name } = unref(purchaseOrder)?.buyer || {};

  if (!_id || props.contactNumber) {
    return null;
  }

  return [
    {
      role: "buyer",
      _id,
      businessName: shop_name,
    },
  ];
});

const onSubmit = async () => {
  try {
    isLoading.value = true;

    const [, conversationContact] = await Promise.all([
      createCustomContact(formData, linkedTo),
      createConversationContact(formData, linkedTo),
    ]);

    emit("contact-created", conversationContact);
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

onBeforeMount(() => {
  if (props.contactNumber) {
    checkIfCoreContact(props.contactNumber);
  }
});
</script>
